import { NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { resolveColor } from 'app/trello/utility/resolve-color';
import { ChartOptions, ChartType, Color, LegendOptions, TooltipOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BaseChartDirective,
    NgIf,
  ],
  selector: 'app-progression-split-chart',
  styleUrls: ['./progression-split-chart.component.css'],
  templateUrl: './progression-split-chart.component.html',
})
export class ProgressionSplitChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input({required: true}) chartValues: {
    [key: string]: {
      label: string,
      percentage: number,
      backgroundColor?: string,
    },
  } = {};

  @Input() chartContainer: HTMLElement | undefined = undefined;

  @ViewChild('canvasContainer') protected canvasContainer!: ElementRef;

  private chartContainerResizeObserver: ResizeObserver | undefined;

  private initialized: boolean = false;
  private initializedView: boolean = false;

  protected readonly mappedChartAxisLabels: string[] = [''];

  protected mappedChartDatasets: {
    data: number[];
    label: string | undefined;
    backgroundColor?: Color;
    borderColor?: Color;
    fill?: boolean;
  }[] = [];

  protected mappedChartDatasetsReady: boolean = false;

  protected readonly chartType: ChartType = 'bar';
  protected readonly chartLegend: boolean = true;
  protected readonly chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    interaction: {
      axis: 'y',
      mode: 'nearest',
      intersect: true,
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          callback: (value: string | number) => `${value}%`,
        },
        max: 100,
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      } as LegendOptions<'bar'>,
      tooltip: {
        position: 'nearest',
        callbacks: {
          label: (context) => {

            const label = context.dataset.label || '';
            const value = context.raw;

            return `${label}: ${value}%`;
          },
        },
      } as TooltipOptions<'bar'>,
      noTooltipColorBoxBorders: {
        enabled: true,
      },
    } as any,
  };

  public ngOnInit(): void {

    this.initialized = true;

    this.prepareData();
  }

  public ngAfterViewInit(): void {

    this.initializedView = true;

    this.keepCanvasSizeSameAsChartContainer();
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartContainer']) {
      this.keepCanvasSizeSameAsChartContainer();
    }

    if (changes['chartValues']) {
      this.prepareData();
    }
  }

  public ngOnDestroy(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }
  }

  private getColorByPercentage(
    percentage: number,
  ): string {

    // calculate a position on a red -> yellow -> green gradient

    const pct = Math.max(0, Math.min(100, percentage));
    const p = pct / 100;

    let r: number, g: number;
    const b = 0; // keep blue at 0

    if (p < 0.5) {

      r = 255;
      g = Math.floor(2 * p * 255);

    } else {
      g = 255;
      r = Math.floor(255 * (2 - 2 * p));
    }

    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  }

  private keepCanvasSizeSameAsChartContainer(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (!this.initializedView
      || !this.chartContainer) {
      return;
    }

    this.chartContainerResizeObserver =
      new ResizeObserver((entries) => {

        for (const entry of entries) {

          if (entry.target === this.chartContainer) {

            let {width, height} = entry.contentRect;

            width = Math.round(width);
            height = Math.round(height);

            if (this.canvasContainer) {

              this.canvasContainer.nativeElement.style.width = `${width}px`;
              this.canvasContainer.nativeElement.style.height = `${height}px`;
            }
          }
        }
      });

    this.chartContainerResizeObserver.observe(this.chartContainer);
  }

  private prepareData(): void {

    if (!this.initialized) {
      return;
    }

    this.mappedChartDatasets = this.toChartDatasets();
    this.mappedChartDatasetsReady = true;
  }

  private toChartDatasets() {

    return Object.values(this.chartValues)
      .map((value) => {

        const backgroundColor =
          value.backgroundColor
            ? resolveColor(value.backgroundColor)
            : this.getColorByPercentage(value.percentage);

        return {
          label: value.label,
          data: [value.percentage],
          backgroundColor,
        };
      });
  }

}
